import { IonSpinner } from '@ionic/react'
import SolarPowerIcon from '@mui/icons-material/SolarPower'
import dayjs from 'dayjs'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { useSolarPanel } from '../../../devices/solarPanel/hooks/useSolarPanel'
import useProductionData from '../../../electricity/charts/productionChart/hooks/useProductionData'
import OverViewCard from '../OverviewCard'
import DisplayInfo from '../carStateCard/components/DisplayInfo'
import PanelProductionChart from './components/PanelProductionChart'

const PanelStateCard: React.FC = () => {
	const history = useHistory()

	const { t } = useTranslation('pages_overview')

	const panel = useSolarPanel({
		span: 'day',
		startTime: dayjs().startOf('day'),
		endTime: dayjs().endOf('day'),
	})

	const onClick = () => {
		if (!panel.inverter) return

		history.push(`/solar-panel/${panel.inverter.id}`)
	}

	const { data: todayData, isLoading: isLoadingToday } = useProductionData({
		from: dayjs().startOf('day').toISOString(),
		to: dayjs().endOf('day').toISOString(),
		timespan: 'hour',
		isDarkMode: true,
	})

	const { data: yesterdayData, isLoading: isLoadingYesterday } = useProductionData({
		from: dayjs().subtract(24, 'hour').startOf('day').toISOString(),
		to: dayjs().subtract(24, 'hour').endOf('day').toISOString(),
		timespan: 'hour',
		isDarkMode: true,
	})

	const combinedProd = [
		...(todayData?.production.data ?? []),
		...(yesterdayData?.production.data ?? []),
	]

	const prodUntilNow = combinedProd
		.filter(x =>
			dayjs(x.date).tz('Europe/Helsinki').isBefore(dayjs().tz('Europe/Helsinki').endOf('hour'))
		)
		.sort((a, b) => dayjs(a.date).diff(dayjs(b.date)))
		.slice(-25)

	const combinedSavings = [
		...(todayData?.estimatedSavings.groupedData ?? []),
		...(yesterdayData?.estimatedSavings.groupedData ?? []),
	]

	const savingsUntilNow =
		combinedSavings
			.filter(x =>
				dayjs(
					`${x.localTime.year}-${x.localTime.month}-${x.localTime.day} ${x.localTime.hour}:00`
				).isBefore(dayjs().tz('Europe/Helsinki').endOf('hour'))
			)
			.sort((a, b) =>
				dayjs(
					`${a.localTime.year}-${a.localTime.month}-${a.localTime.day} ${a.localTime.hour}:00`
				).diff(
					dayjs(
						`${b.localTime.year}-${b.localTime.month}-${b.localTime.day} ${b.localTime.hour}:00`
					)
				)
			)
			.slice(-25)
			.reduce((acc, curr) => acc + curr.value, 0) / 100

	const isLoading = isLoadingToday || isLoadingYesterday || panel.isLoading

	return (
		<OverViewCard
			headerText={t('panelCard.otsikko')}
			icon={<SolarPowerIcon />}
			iconType='mui'
			className='car-state-card'
			onClick={onClick}
			headerSubtext={t('panelCard.last24hours')}
		>
			<div className='card-content'>
				{isLoading ? (
					<div className='solar-panel-chart flex justify-center items-center'>
						<IonSpinner />
					</div>
				) : prodUntilNow?.length ? (
					<>
						<div className='solar-panel-chart'>
							<PanelProductionChart chartData={prodUntilNow} />
						</div>
						<div className='info'>
							<DisplayInfo
								title={t('panelCard.now')}
								text={`${((panel.inverter?.productionState.productionRate ?? prodUntilNow[prodUntilNow.length - 1].value) * 1000).toFixed(0)} W`}
								divider
							/>
							<DisplayInfo
								title={t('panelCard.maxPower')}
								text={`${Math.max(...prodUntilNow.map((e: any) => e.value * 1000)).toFixed(0)} W`}
								divider
							/>
							<DisplayInfo
								title={t('panelCard.production')}
								text={`${prodUntilNow.reduce((acc: any, item: any) => acc + item.value, 0).toFixed(0)} kWh`}
								divider
							/>
							<DisplayInfo
								title={t('panelCard.saving')}
								text={`${savingsUntilNow.toFixed(2).replace('.', ',')} €`}
							/>
						</div>
					</>
				) : (
					<div className='text-text-grey-200 dark:text-text-grey-100 text-center'>
						{t('panelCard.sahkonTuotto')}
					</div>
				)}
			</div>
		</OverViewCard>
	)
}

export default PanelStateCard
